import { useState, useEffect } from "react";
import { Modal } from "../primitives/Modal";
import { Textarea } from "../primitives/Textarea";
import { Button } from "../primitives/Button";
import { FileText, Phone, Users, AlertCircle, MoreHorizontal } from "lucide-react";
import { type NoteType, type Note, type NoteCreate, type NoteUpdate } from "../../api/notes";
import { cn } from "../utils/cn";

type NoteFormModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: NoteCreate | NoteUpdate) => Promise<void>;
  entityType: string;
  entityId: string;
  note?: Note | null;
  initialNoteType?: NoteType;
};

const NOTE_TYPES: { value: NoteType; label: string; icon: React.ReactNode }[] = [
  { value: "general", label: "Notes", icon: <FileText size={14} strokeWidth={1.5} /> },
  { value: "call", label: "Appel", icon: <Phone size={14} strokeWidth={1.5} /> },
  { value: "meeting", label: "Rencontre", icon: <Users size={14} strokeWidth={1.5} /> },
  { value: "complaint", label: "Plainte", icon: <AlertCircle size={14} strokeWidth={1.5} /> },
  { value: "other", label: "Autre", icon: <MoreHorizontal size={14} strokeWidth={1.5} /> },
];

export function NoteFormModal({
  open,
  onClose,
  onSubmit,
  entityType,
  entityId,
  note,
  initialNoteType = "general",
}: NoteFormModalProps) {
  const [noteType, setNoteType] = useState<NoteType>(initialNoteType);
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (note) {
      setNoteType(note.note_type);
      setContent(note.content);
    } else {
      setNoteType(initialNoteType);
      setContent("");
    }
    setError(null);
  }, [note, initialNoteType, open]);

  const handleSubmit = async () => {
    if (!content.trim()) {
      setError("Le contenu de la note est requis");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (note) {
        await onSubmit({ note_type: noteType, content });
      } else {
        await onSubmit({
          entity_type: entityType,
          entity_id: entityId,
          note_type: noteType,
          content,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={note ? "Modifier la note" : "Nouvelle note"}
      open={open}
      onClose={onClose}
      showDefaultButtons={false}
      showCloseButton={true}
      size="lg"
    >
      <div className="space-y-4">
        {/* Type selection tabs */}
        <div>
          <label className="text-xs text-muted mb-2 block">Type de note</label>
          <div className="flex gap-2 flex-wrap">
            {NOTE_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => setNoteType(type.value)}
                className={cn(
                  "px-3 py-1.5 rounded-md text-xs font-medium transition flex items-center gap-1.5",
                  noteType === type.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-surface-muted text-text hover:bg-surface"
                )}
              >
                {type.icon}
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content textarea */}
        <div>
          <label className="text-xs text-muted mb-2 block">Contenu</label>
          <textarea
            className="w-full min-h-[120px] rounded-md border border-border bg-surface px-3 py-2 text-sm text-text resize-y"
            placeholder="Rédigez votre note..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={saving}
          />
        </div>

        {error && (
          <div className="rounded-md border border-danger bg-danger/10 p-3">
            <div className="text-xs text-danger">{error}</div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <Button label="Annuler" intent="default" size="sm" onClick={onClose} disabled={saving} />
          <Button
            label={note ? "Enregistrer" : "Créer"}
            intent="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={saving || !content.trim()}
          />
        </div>
      </div>
    </Modal>
  );
}
